import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsTooltipDirective,
} from '@rds/angular-components';
import { EMPTY, Observable, catchError, tap } from 'rxjs';

import { RoomsListModel } from '@bookly/shared';
import { FavoriteService } from '@bookly-user/rooms/services/favorite.service';
import { RoomsService } from '@bookly-user/rooms/services/rooms.service';

@Component({
  selector: 'bus-room-card',
  imports: [
    NgOptimizedImage,
    RdsIconComponent,
    RdsTooltipDirective,
    RdsIconButtonComponent,
    RouterLink,
  ],
  templateUrl: './room-card.component.html',
  styleUrl: './room-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomCardComponent {
  readonly #favoriteService = inject(FavoriteService);
  readonly #roomsService = inject(RoomsService);

  @Input() public set roomData(value: RoomsListModel) {
    if (value) {
      this.room = value;
      this.isFavorite.set(value.isFavorite ?? false);
      this.features = value.features?.map(item => item.name).join(', ');
    }
  }

  protected room!: RoomsListModel;
  protected features!: string;
  protected readonly isFavorite = signal(false);

  /** Emits the room's dexId when the room is successfully removed from favorites. */
  @Output() readonly unfavorited = new EventEmitter<string>();

  protected toggleFavorite(event: PointerEvent): void {
    event.stopPropagation();
    const currentIsFavorite = this.isFavorite();

    const request$ = (
      currentIsFavorite
        ? this.#favoriteService.removeFavorite(this.room.dexId)
        : this.#favoriteService.addFavorite(this.room.dexId)
    ) as Observable<unknown>;

    request$
      .pipe(
        tap(() => {
          this.isFavorite.set(!currentIsFavorite);
          this.#roomsService.updateRoomFavorite(
            this.room.dexId,
            !currentIsFavorite
          );
          if (currentIsFavorite) {
            this.unfavorited.emit(this.room.dexId);
          }
        }),
        catchError(() => EMPTY)
      )
      .subscribe();
  }
}
